import { NetworkStatus, useLazyQuery, useQuery } from '@apollo/client';
import Vault from 'common/components/Farm/Vault';
import { AppSyncFarm, MonitorVaults } from 'common/models/farm';
import { QueryItemResponse, QueryItemsResponse } from 'common/models/graphql';
import { AppSyncUserInfo } from 'common/models/userInfo';
import {
  API_GATEWAY_URL,
  ChainId,
  HODL_VAULT_TABLE,
  REFRESH_INTERVALS,
  UPDATE_POOL_URL,
} from 'constants/constants';
import { getFarmQuery, GetFarmVariables } from 'graphql/queries/getFarm.query';
import {
  getUserInfoQuery,
  GetUserInfoVariables,
} from 'graphql/queries/getUserInfo.query';
import { listFarmsOnChainQuery } from 'graphql/queries/listFarms.query';
import useParsedQueryString from 'hooks/useParsedQueryString';
import isNumber from 'lodash/isNumber';
import React, { FC, useEffect, useMemo, useState } from 'react';
import useFetch from 'use-http';
import debounce from 'lodash/debounce';
import { TokenUtils } from 'utils/tokenUtils';
import VaultsTable from 'common/components/VaultsTable/VaultsTable';
import NewsContainer from './NewsContainer';
import { listMonitorVaultsQuery } from 'graphql/queries/listMonitorVaults.query';
import { Search } from 'react-feather';
import Input from 'common/components/Input';
import { FarmUtils } from 'utils/farmUtils';
import Submenu from 'common/components/Submenu';
import UserDepositsTable from 'common/components/VaultsTable/UserDepositsTable';
import useSelectedChain from 'hooks/useSelectedChain';
import { useAccount } from 'wagmi';
import { RouteComponentProps } from '@reach/router';

const IchiVaultContainer: FC<RouteComponentProps> = () => {
  const { tab: urlTab } = useParsedQueryString();
  const { chainId } = useSelectedChain();
  const { address } = useAccount();
  const [searchValue, setSearchValue] = useState<string>('');
  const [selectedTab, setSelectedTab] = useState<string>(
    urlTab?.toString() === 'mydeposits' ? 'My Deposits' : 'Vaults'
  );
  const [selectedTokenAddress, setSelectedTokenAddress] = useState<string>('');

  const { poolId: urlPoolId, address: urlAddress } = useParsedQueryString();

  // TODO: Handle loading and error
  const { data: dataFarms, refetch: refetchFarms } = useQuery<
    QueryItemsResponse<AppSyncFarm>
  >(listFarmsOnChainQuery(chainId), {
    // Fetch the list of farms if there is no urlPoolId
    fetchPolicy: 'cache-and-network',
    skip: urlPoolId != null && urlPoolId !== '' && isNumber(+urlPoolId),
    pollInterval: REFRESH_INTERVALS['listFarms'],
  });
  const { data: dataMonitorVaults } = useQuery<
    QueryItemsResponse<MonitorVaults>
  >(listMonitorVaultsQuery, {
    fetchPolicy: 'cache-and-network',
    skip:
      urlPoolId != null &&
      urlPoolId !== '' &&
      isNumber(+urlPoolId) &&
      urlAddress != null &&
      urlAddress !== '',
    pollInterval: REFRESH_INTERVALS['listMonitorVaults'],
  });
  const { data: dataFarm } = useQuery<
    QueryItemResponse<AppSyncFarm>,
    GetFarmVariables
  >(getFarmQuery, {
    // Fetch the list of farms if there is no urlPoolId
    fetchPolicy: 'cache-and-network',
    skip: urlPoolId == null || urlPoolId === '' || !isNumber(+urlPoolId),
    variables: {
      poolId: Number(urlPoolId),
    },
    pollInterval: REFRESH_INTERVALS['getFarm'],
  });
  const [
    loadUserInfo,
    {
      data: dataUserInfo,
      networkStatus: networkStatusUserInfo,
      refetch: refetchUserInfo,
    },
  ] = useLazyQuery<QueryItemResponse<AppSyncUserInfo>, GetUserInfoVariables>(
    getUserInfoQuery,
    {
      // https://github.com/apollographql/react-apollo/issues/321
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
      variables: {
        user: address as string,
        chainId: chainId,
      },
      pollInterval: REFRESH_INTERVALS['userInfo'],
    }
  );

  const { get } = useFetch(API_GATEWAY_URL, {
    mode: 'no-cors',
  });

  const updatePool = async (poolId: number): Promise<void> => {
    try {
      await get(`${UPDATE_POOL_URL}?poolId=${poolId}`);
      await refetchFarms();
    } catch (e) {
      console.warn('Could not update product farms', e);
    }
  };

  const debouncedUpdatePool = useMemo(
    () => debounce(updatePool, 750),
    []
  ) as unknown as (poolId: number) => Promise<void>;

  const executeRefetchUserInfo = async (): Promise<any> => {
    if (address && refetchUserInfo) {
      await refetchUserInfo();
    }
  };
  const debouncedExecuteRefetchUserInfo = useMemo(
    () => debounce(executeRefetchUserInfo, 750),
    []
  ) as unknown as () => Promise<void>;

  const executeLoadUserInfo = async (): Promise<any> => {
    if (address) {
      await loadUserInfo();
    }
    return Promise.resolve();
  };

  useEffect(() => {
    if (chainId && address) {
      void executeLoadUserInfo();
    }
  }, [address, chainId]);

  // const handleFilter = () => {
  //   setIsFiltered(!isFiltered);
  // };

  const monitorVaults: MonitorVaults[] =
    dataMonitorVaults?.listMonitorVaults.items || [];

  const substantialTvl = chainId === ChainId.Mainnet ? 1000 : 5000;
  // show vaults with substantial TVL on top, sorted by irr
  // show vaults with not substantial TVL at the bottom of the table, sorted by TVL
  const appSyncFarms: AppSyncFarm[] =
    dataFarms?.listFarms?.items
      .filter((item) => item.isPosition && !item.isHidden && !FarmUtils.isPancakeVault(item, monitorVaults))
      .sort((a, b) => {
        const isAsubstantial = a.tvl > substantialTvl;
        const isBsubstantial = b.tvl > substantialTvl;
        let result = 0;
        if (isAsubstantial && !isBsubstantial) result = -1;
        if (!isAsubstantial && isBsubstantial) result = 1;
        if (isAsubstantial && isBsubstantial)
          result =
            (FarmUtils.getFeeAprByVaultAddress(monitorVaults, b.lpAddress) ?? 0) -
            (FarmUtils.getFeeAprByVaultAddress(monitorVaults, a.lpAddress) ?? 0);
        if (!isAsubstantial && !isBsubstantial) result = b.tvl - a.tvl;
        return result;
      }) || [];
  const table = HODL_VAULT_TABLE;
  const appSyncUserInfo: AppSyncUserInfo = dataUserInfo?.userInfo || {
    farms: [],
    tokens: [],
  };

  if (urlPoolId || dataFarm?.getFarm) {
    const farmUserInfo = TokenUtils.getFarmUserInfo(
      dataFarm?.getFarm as AppSyncFarm,
      dataUserInfo?.userInfo as AppSyncUserInfo
    );

    if (!dataFarm?.getFarm?.isPosition) {
      return null;
    }
    const monitorVault = FarmUtils.getMonitorVaultByAddress(
      monitorVaults,
      dataFarm?.getFarm.lpAddress
    );
    const vaultIRR = monitorVault ? monitorVault.vaultIrrAllTx : 0;
    const irr = dataFarm?.getFarm.tvl < 10000 && vaultIRR < 1 ? 15 : vaultIRR;

    return (
      <Vault
        farm={dataFarm?.getFarm}
        farmUserInfo={farmUserInfo}
        refetchUserInfo={debouncedExecuteRefetchUserInfo}
        updatePool={debouncedUpdatePool}
        loadingUserInfo={networkStatusUserInfo === NetworkStatus.refetch}
        type="hodlvault"
        vaultIRR={irr}
      />
    );
  }

  if (urlAddress) {
    const farmByAddress = dataFarms?.listFarms?.items.filter(
      (f) => f.lpAddress.toLowerCase() === urlAddress.toString().toLowerCase()
    )[0];
    const farmUserInfo = TokenUtils.getFarmUserInfo(
      farmByAddress as AppSyncFarm,
      dataUserInfo?.userInfo as AppSyncUserInfo
    );

    if (!farmByAddress?.isPosition) {
      return null;
    }

    const monitorVault = FarmUtils.getMonitorVaultByAddress(
      monitorVaults,
      farmByAddress.lpAddress
    );
    const vaultIRR = monitorVault ? monitorVault.vaultIrrAllTx : 0;
    const irr = farmByAddress.tvl < 10000 && vaultIRR < 1 ? 15 : vaultIRR;

    return (
      <Vault
        farm={farmByAddress ?? null}
        farmUserInfo={farmUserInfo}
        refetchUserInfo={debouncedExecuteRefetchUserInfo}
        updatePool={debouncedUpdatePool}
        loadingUserInfo={networkStatusUserInfo === NetworkStatus.refetch}
        type="hodlvault"
        vaultIRR={irr}
      />
    );
  }

  const backToVaults = () => {
    setSelectedTab('Vaults');
    setSelectedTokenAddress('');
  };

  return (
    <>
      {/* <NewsContainer /> */}
      {/* <div className="section-description">
        {TableDescriptions.hodlVaults.description}
        <OutboundLink target="_blank" href={TableDescriptions.hodlVaults.link} className="mt-6">
          <Image key="second in pair" className="icon-new-window" alt="new window icon" marginLeft="5px" />
        </OutboundLink>
      </div> */}
      {!selectedTokenAddress && (
        <Submenu
          tab={selectedTab}
          handleClick={setSelectedTab}
          accountAddress={address}
        />
      )}
      {selectedTab === 'Vaults' && (
        <>
          <div className="flex flex-row justify-start items-center gap-3">
            <Input
              label=""
              onChange={(val) => setSearchValue(val)}
              icon={<Search color={'#838BA8'} />}
              placeholder="Search"
              className="mt-3 mb-5 w-64 h-10 rounded-[10px]"
            />
          </div>
          <VaultsTable
            table={table}
            farms={appSyncFarms}
            // farms={appSyncFarms}
            monitorVaults={monitorVaults}
            userInfo={appSyncUserInfo}
            userInfoLoading={networkStatusUserInfo === NetworkStatus.refetch}
            refetchUserInfo={debouncedExecuteRefetchUserInfo}
            isIchiVault={true}
            searchValue={searchValue}
          />
        </>
      )}
      {selectedTab === 'My Deposits' && (
        <UserDepositsTable
          farms={appSyncFarms}
          // farms={appSyncFarms}
          monitorVaults={monitorVaults}
          userInfo={appSyncUserInfo}
          userInfoLoading={networkStatusUserInfo === NetworkStatus.refetch}
          refetchUserInfo={debouncedExecuteRefetchUserInfo}
          isIchiVault={true}
          searchValue={searchValue}
          selectedTokenAddress={selectedTokenAddress}
          backToVaults={backToVaults}
        />
      )}
    </>
  );
};

export default IchiVaultContainer;
